<template>
  <div class="card main-card">
    <!-- Modal eliminar credencial API -->
    <vs-dialog not-center v-model="modalDeleteApiCredential">
      <template #header>
        <h4 class="not-margin">
          Delete <b>API Credential</b>
        </h4>
      </template>
      <div class="con-content">
        <p>
          Are you sure you want to delete this API credential?
        </p>
        <p><strong>Provider:</strong> {{ selectedApiCredential.provider }}</p>
        <p><strong>Name:</strong> {{ selectedApiCredential.name || 'No name' }}</p>
      </div>
      <template #footer>
        <div class="container">
          <div class="row">
            <div class="col-6 d-flex align-items-center justify-content-center">
              <vs-button @click="deleteApiCredential()" transparent>
                Yes!
              </vs-button>
            </div>
            <div class="col-6 d-flex align-items-center justify-content-center">
              <vs-button @click="modalDeleteApiCredential=false" danger transparent>
                Cancel
              </vs-button>
            </div>
          </div>
        </div>
      </template>
    </vs-dialog>

    <div class="card-body">
      <h4>API Credentials Management</h4>
      <p class="smallText">Manage API credentials for this user. Store credentials for different providers like ICC, AWS, Stripe, Firebase, etc. All credentials are stored securely and can be activated or deactivated as needed.</p>
      
      <!-- Form para crear/editar credenciales -->
      <div class="form-group">
        <div class="row">
          <div class="col-md-4">
            <p class="text-muted">Provider *</p>
            <select class="form-control" v-model="apiForm.provider">
              <option value="">Select provider</option>
              <option value="ICC">ICC</option>
            </select>
            <small class="form-text text-muted">Select the API provider (required)</small>
          </div>
          <div class="col-md-4">
            <p class="text-muted">Name (alias)</p>
            <input type="text" class="form-control" placeholder="e.g., ICC Farm #1, Production AWS" v-model.trim="apiForm.name" />
            <small class="form-text text-muted">Optional: Give a descriptive name to identify this credential</small>
          </div>
          <div class="col-md-4">
            <p class="text-muted">Status</p>
            <select class="form-control" v-model="apiForm.status">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <small class="form-text text-muted">Active credentials can be used by the system</small>
          </div>
        </div>
        <hr>
        <!-- Credentials dinámicas -->
        <div class="mt-3">
          <div class="mb-2">
            <p class="text-muted mb-0">Credentials *</p>
            <small class="form-text text-muted">
              Add the required fields for your API.<br>
              <strong>ICC requires:</strong> url, username, password, client_id, client_secret
            </small>
          </div>
          
          <div v-for="(field, index) in apiForm.credentials" :key="index" class="row mb-2">
            <div class="col-md-5">
              <input type="text" class="form-control" :placeholder="keyPlaceholder" v-model="field.key" />
            </div>
            <div class="col-md-6">
              <input type="text" class="form-control" :placeholder="valuePlaceholder" v-model="field.value" />
            </div>
            <div class="col-md-1 d-flex align-items-center justify-content-center">
              <vs-button transparent @click="removeCredentialField(index)" size="small" v-if="apiForm.credentials.length > 1">
                ❌ Delete
              </vs-button>
            </div>
          </div>
          
          <div class="row mb-2">
            <div class="col-md-12 d-flex align-items-center justify-content-start">
              <vs-button  @click="addCredentialField()" size="small" color="success">
                <span class="material-icons">add</span> Add Field
              </vs-button>
            </div>
          </div>
        </div>
        
        <div class="mt-3">
          <!-- Test Credentials button for ICC -->
          <div v-if="canTestIccConnection" class="card mb-2">
            <div class="card-body">
                <vs-button 
                  @click="testIccConnection()" 
                  :disabled="isTestingConnection" 
                  color="success"
                >
                  <span class="material-icons">wifi_tethering</span> 
                  {{ isTestingConnection ? 'Testing...' : 'Test Credentials' }}
                </vs-button>
                <small class="form-text text-muted d-block">Test your ICC connection before saving</small>
            </div>
          </div>
          
          <vs-button @click="saveApiCredential()" v-if="!editingApiCredential">Save Credential</vs-button>
        <div v-else class="d-flex align-items-center">
            <vs-button @click="updateApiCredential()" class="me-2">Update Credential</vs-button>
            <vs-button transparent @click="cancelEditApiCredential()">Cancel</vs-button>
        </div>
        </div>
      </div>

      <!-- Tabla de credenciales -->
      <div v-if="apiCredentials.length > 0">
        <h5 class="mt-4 mb-2">User's API Credentials</h5>
        <p class="smallText text-muted mb-3">Manage the stored API credentials for this user. You can edit, activate/deactivate, or delete credentials as needed.</p>
        <table class="table table-striped">
          <thead>
            <tr>
              <th>Provider</th>
              <th>Name</th>
              <th>Fields</th>
              <th>Status</th>
              <th>Created</th>
              <th>Operations</th>
            </tr>
          </thead>
        <tbody>
          <tr v-for="credential in apiCredentials" :key="credential.id">
            <td>
              <strong>{{ credential.provider }}</strong>
            </td>
            <td>{{ credential.name || '-' }}</td>
            <td>
              <span v-for="(value, key) in credential.credentials" :key="key" class=" me-1 mb-1">
                {{ key }}
              </span>
            </td>
            <td>
              <span :class="credential.status === 'active' ? 'text-success' : 'text-muted'">
             {{ credential.status }}
              </span>
            </td>
            <td>{{ formatDate(credential.createdAt) }}</td>
            <td>
              <a class="text-link smallText" @click="editApiCredential(credential)">
                <span class="material-icons smallText me-1">edit</span>Edit
              </a> <br>
              <a class="text-link smallText" @click="toggleCredentialStatus(credential)" >
                <span class="material-icons smallText me-1">{{ credential.status === 'active' ? 'pause' : 'play_arrow' }}</span>{{ credential.status === 'active' ? 'Deactivate' : 'Activate' }}
              </a> <br>
              <a class="text-link smallText" @click="deleteApiCredentialModal(credential)">
                <span class="material-icons smallText me-1">delete</span>Delete
              </a>
            </td>
          </tr>
          </tbody>
        </table>
      </div>      <div v-if="apiCredentials.length === 0" class="text-center mt-4 mb-4">
        <div class="alert alert-info">
          <h5><span class="material-icons">info</span> No API credentials configured yet</h5>
          <p class="mb-0">Create your first API credential using the form above. You can store credentials for multiple providers and manage them easily.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import '../mixins/valitators.js';
var config = require("@/config/api.json");
const API = config.api;

export default {
  name: "ApiCredentials",
  props: {
    userId: {
      type: [String, Number],
      required: true
    },
    uid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      apiCredentials: [],
      editingApiCredential: null,
      apiForm: {
        provider: '',
        name: '',
        credentials: [{ key: '', value: '' }],
        status: 'active'
      },
      modalDeleteApiCredential: false,
      selectedApiCredential: {},
      isTestingConnection: false
    };
  },
  computed: {
    canTestIccConnection() {
      if (this.apiForm.provider !== 'ICC') return false;
      
      const credentialsObj = this.buildCredentialsObject();
      const requiredFields = ['url', 'username', 'password', 'client_id', 'client_secret'];
      
      return requiredFields.every(field => 
        credentialsObj[field] && credentialsObj[field].trim() !== ''
      );
    },
    
    keyPlaceholder() {
      if (this.apiForm.provider === 'ICC') {
        return 'Field name (url, username, password, client_id, client_secret)';
      }
      return 'Field name (e.g., api_key, username)';
    },
    
    valuePlaceholder() {
      return 'Enter the credential value';
    }
  },
  mounted() {
    this.loadApiCredentials();
  },
  methods: {
    formatDate(dateString) {
      return this.moment(dateString).format('MMM DD, YYYY');
    },

    async loadApiCredentials() {
      if (!this.userId) {
        this.notify('error', 'User information not found');
        return;
      }

      try {
        const response = await axios.get(`${API}/api-credentials/${this.userId}`);
        this.apiCredentials = response.data;
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.apiCredentials = [];
          console.log("response 404", error);
          this.notify('info', 'No API credentials found for this user');
        } else {
          console.error('Error loading API credentials:', error);
          this.notify('error', 'Error loading API credentials');
        }
      }
    },

    addCredentialField() {
      this.apiForm.credentials.push({ key: '', value: '' });
    },

    removeCredentialField(index) {
      this.apiForm.credentials.splice(index, 1);
    },

    resetApiForm() {
      this.apiForm = {
        provider: '',
        name: '',
        credentials: [{ key: '', value: '' }],
        status: 'active'
      };
      this.editingApiCredential = null;
    },

    validateApiForm() {
      if (!this.vEmpty(this.apiForm.provider, 'Provider')) {
        return false;
      }

      // Validar que al menos hay un campo de credencial con key y value
      const validCredentials = this.apiForm.credentials.filter(
        cred => cred.key.trim() !== '' && cred.value.trim() !== ''
      );

      if (validCredentials.length === 0) {
        this.notify('error', 'At least one credential field is required');
        return false;
      }

      // Validación específica para ICC
      if (this.apiForm.provider === 'ICC') {
        const credentialsObj = this.buildCredentialsObject();
        const requiredFields = ['url', 'username', 'password', 'client_id', 'client_secret'];
        const missingFields = requiredFields.filter(field => 
          !credentialsObj[field] || credentialsObj[field].trim() === ''
        );

        if (missingFields.length > 0) {
          this.notify('error', `ICC provider requires these fields: ${missingFields.join(', ')}`);
          return false;
        }
      }

      return true;
    },

    buildCredentialsObject() {
      const credentialsObj = {};
      this.apiForm.credentials.forEach(cred => {
        if (cred.key.trim() && cred.value.trim()) {
          credentialsObj[cred.key.trim()] = cred.value.trim();
        }
      });
      return credentialsObj;
    },

    async saveApiCredential() {
      if (!this.validateApiForm()) {
        return;
      }

      if (!this.userId) {
        this.notify('error', 'User information not found');
        return;
      }

      const credentialsObj = this.buildCredentialsObject();

      const payload = {
        user_id: this.userId,
        provider: this.apiForm.provider.trim(),
        name: this.apiForm.name.trim() || null,
        credentials: credentialsObj,
        status: this.apiForm.status
      };

      this.openLoading();

      try {
        await axios.post(`${API}/api-credential`, payload);
        this.notify('success', 'API credential saved successfully');
        this.resetApiForm();
        await this.loadApiCredentials();
      } catch (error) {
        console.error('Error saving API credential:', error);
        this.notify('error', error.response?.data?.error || 'Error saving API credential');
      } finally {
        this.closeLoading();
      }
    },

    editApiCredential(credential) {
      this.editingApiCredential = credential;
      this.apiForm.provider = credential.provider;
      this.apiForm.name = credential.name || '';
      this.apiForm.status = credential.status;
      
      // Convertir el objeto credentials a array de key-value
      this.apiForm.credentials = [];
      Object.entries(credential.credentials).forEach(([key, value]) => {
        this.apiForm.credentials.push({ key, value });
      });
    },

    cancelEditApiCredential() {
      this.resetApiForm();
    },

    async updateApiCredential() {
      if (!this.validateApiForm()) {
        return;
      }

      const credentialsObj = this.buildCredentialsObject();

      const payload = {
        provider: this.apiForm.provider.trim(),
        name: this.apiForm.name.trim() || null,
        credentials: credentialsObj,
        status: this.apiForm.status
      };

      this.openLoading();

      try {
        await axios.put(`${API}/api-credential/${this.editingApiCredential.id}`, payload);
        this.notify('success', 'API credential updated successfully');
        this.resetApiForm();
        await this.loadApiCredentials();
      } catch (error) {
        console.error('Error updating API credential:', error);
        this.notify('error', error.response?.data?.error || 'Error updating API credential');
      } finally {
        this.closeLoading();
      }
    },

    async testIccConnection() {
      const credentialsObj = this.buildCredentialsObject();
      
      if (!this.canTestIccConnection) {
        this.notify('error', 'Please fill in all required ICC fields: url, username, password, client_id, client_secret');
        return;
      }

      this.isTestingConnection = true;

      try {
        const params = new URLSearchParams({
          url: credentialsObj.url,
          username: credentialsObj.username,
          password: credentialsObj.password,
          client_id: credentialsObj.client_id,
          client_secret: credentialsObj.client_secret
        });

        const response = await axios.get(`${API}/api-credentials/test-connection?${params.toString()}`);
        
        if (response.data.success) {
          this.notify('success', `ICC connection successful! Token preview: ${response.data.tokenSample}`);
        } else {
          this.notify('error', response.data.message || 'ICC connection failed');
        }
      } catch (error) {
        console.error('Error testing ICC connection:', error);
        const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to test ICC connection';
        this.notify('error', errorMessage);
      } finally {
        this.isTestingConnection = false;
      }
    },

    deleteApiCredentialModal(credential) {
      this.selectedApiCredential = credential;
      this.modalDeleteApiCredential = true;
    },

    async deleteApiCredential() {
      this.openLoading();

      try {
        await axios.delete(`${API}/api-credential/${this.selectedApiCredential.id}`);
        this.notify('success', 'API credential deleted successfully');
        this.modalDeleteApiCredential = false;
        this.selectedApiCredential = {};
        await this.loadApiCredentials();
      } catch (error) {
        console.error('Error deleting API credential:', error);
        this.notify('error', error.response?.data?.error || 'Error deleting API credential');
      } finally {
        this.closeLoading();
      }
    },

    async toggleCredentialStatus(credential) {
      const newStatus = credential.status === 'active' ? 'inactive' : 'active';
      
      this.openLoading();

      try {
        await axios.patch(`${API}/api-credential/${credential.id}/status`, { status: newStatus });
        this.notify('success', `Credential ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
        await this.loadApiCredentials();
      } catch (error) {
        console.error('Error changing credential status:', error);
        this.notify('error', error.response?.data?.error || 'Error changing credential status');
      } finally {
        this.closeLoading();
      }
    }
  }
};
</script>
